import { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

function toMillis(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (typeof v?.seconds === "number") return v.seconds * 1000;
  const parsed = Date.parse(String(v));
  return Number.isFinite(parsed) ? parsed : 0;
}

function num(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n : 0;
}

function digits(v) {
  return String(v || "").replace(/\D/g, "");
}

function normalizeWalletRole(raw) {
  const value = String(raw || "").trim().toLowerCase();
  if (value.includes("mentor")) return "mentor";
  if (value.includes("artist")) return "artist";
  return "";
}

function roleLabel(roles = []) {
  const order = ["user", "artist", "mentor"];
  const labels = { user: "User", artist: "Artist", mentor: "Mentor" };
  return order.filter((role) => roles.includes(role)).map((role) => labels[role]).join(", ") || "User";
}

function artistWalletPhone(id, data = {}) {
  return digits(
    data.user_uid_normalized ||
      data.user_uid ||
      data.userPhone ||
      data.user_phone ||
      data.ownerPhone ||
      data.phone ||
      id
  );
}

function mentorWalletPhone(id, data = {}, artistPhoneById = new Map()) {
  const direct = digits(
    data.user_uid_normalized ||
      data.user_uid ||
      data.userPhone ||
      data.user_phone ||
      data.ownerPhone ||
      data.phone ||
      data.mobileNumber
  );
  if (direct) return direct;
  return artistPhoneById.get(String(data.artistId || id || "")) || "";
}

function normalizedDateStart(raw) {
  const value = String(raw || "").trim();
  if (!value) return 0;
  const ms = Date.parse(`${value}T00:00:00`);
  return Number.isFinite(ms) ? ms : 0;
}

function normalizedDateEnd(raw) {
  const value = String(raw || "").trim();
  if (!value) return Number.MAX_SAFE_INTEGER;
  const ms = Date.parse(`${value}T23:59:59`);
  return Number.isFinite(ms) ? ms : Number.MAX_SAFE_INTEGER;
}

function boolFromFilter(raw) {
  const value = String(raw || "").toLowerCase();
  if (!value || value === "all") return null;
  if (["true", "blocked", "yes"].includes(value)) return true;
  if (["false", "active", "no"].includes(value)) return false;
  return null;
}

function normalizePayoutAccounts(data = {}) {
  const accounts = Array.isArray(data.walletPayoutAccounts) ?
    data.walletPayoutAccounts :
    [];
  const primaryId = String(data.walletPrimaryAccountId || "");
  const legacy = data.walletPayoutDetails && typeof data.walletPayoutDetails === "object" ?
    data.walletPayoutDetails :
    null;

  const list = accounts.map((acc, idx) => {
    const id = String(acc?.id || acc?.accountId || `acc_${idx + 1}`);
    return {
      id,
      method: String(acc?.method || acc?.type || ""),
      holderName: String(acc?.holderName || acc?.accountHolder || ""),
      accountNumber: String(acc?.accountNumber || ""),
      ifsc: String(acc?.ifsc || ""),
      upiId: String(acc?.upiId || ""),
      accountLabel: String(acc?.accountLabel || ""),
      isPrimary: Boolean(acc?.isPrimary || (primaryId && primaryId === id)),
      isActive: acc?.isActive === false ? false : true,
      nameVerified: acc?.nameVerified === false ? false : Boolean(acc?.nameVerified),
      adminNote: String(acc?.adminNote || ""),
      raw: acc || {},
    };
  });

  if (!list.length && legacy) {
    list.push({
      id: "legacy_primary",
      method: String(legacy.method || legacy.type || ""),
      holderName: String(legacy.accountHolder || legacy.holderName || ""),
      accountNumber: String(legacy.accountNumber || ""),
      ifsc: String(legacy.ifsc || ""),
      upiId: String(legacy.upiId || ""),
      accountLabel: "Legacy Primary",
      isPrimary: true,
      isActive: true,
      nameVerified: Boolean(legacy.nameVerified),
      adminNote: "",
      raw: legacy,
    });
  }

  if (!list.some((a) => a.isPrimary) && list.length) {
    list[0].isPrimary = true;
  }
  return list;
}

function walletUserShape(id, data = {}, profile = {}) {
  const roles = Array.isArray(profile.roles) && profile.roles.length ? profile.roles : ["user"];
  return {
    phone: String(data.phone || id || ""),
    name: String(data.name || data.fullName || data.full_name || ""),
    role: roleLabel(roles),
    roles,
    artistIds: profile.artistIds || [],
    mentorIds: profile.mentorIds || [],
    walletBalance: num(data.walletBalance),
    walletOnHold: num(data.walletOnHold),
    walletBlocked: Boolean(data.walletBlocked),
    walletLifetimeEarnings: num(data.walletLifetimeEarnings),
    walletUpdatedAt: data.walletUpdatedAt || data.updatedAt || null,
    walletPayoutDetails: data.walletPayoutDetails || {},
    walletPayoutAccounts: normalizePayoutAccounts(data),
    walletPrimaryAccountId: String(data.walletPrimaryAccountId || ""),
    raw: data,
  };
}

function buildWalletRows(userDocs = [], artistDocs = [], mentorDocs = []) {
  const profileByPhone = new Map();
  const artistPhoneById = new Map();

  const ensureProfile = (phone) => {
    const cleanPhone = digits(phone);
    if (!cleanPhone) return null;
    if (!profileByPhone.has(cleanPhone)) {
      profileByPhone.set(cleanPhone, {
        roles: new Set(["user"]),
        artistIds: [],
        mentorIds: [],
      });
    }
    return profileByPhone.get(cleanPhone);
  };

  userDocs.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const phone = digits(data.phone || docSnap.id);
    const profile = ensureProfile(phone);
    const explicitRole = normalizeWalletRole(data.role || data.userRole || data.type || data.userType);
    if (profile && explicitRole) profile.roles.add(explicitRole);
  });

  artistDocs.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const phone = artistWalletPhone(docSnap.id, data);
    if (!phone) return;
    artistPhoneById.set(docSnap.id, phone);
    const profile = ensureProfile(phone);
    if (!profile) return;
    profile.roles.add("artist");
    profile.artistIds.push(docSnap.id);
  });

  mentorDocs.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const phone = mentorWalletPhone(docSnap.id, data, artistPhoneById);
    if (!phone) return;
    const profile = ensureProfile(phone);
    if (!profile) return;
    profile.roles.add("mentor");
    profile.mentorIds.push(docSnap.id);
  });

  return userDocs.map((docSnap) => {
    const data = docSnap.data() || {};
    const phone = digits(data.phone || docSnap.id);
    const profile = profileByPhone.get(phone) || { roles: new Set(["user"]), artistIds: [], mentorIds: [] };
    return walletUserShape(docSnap.id, data, {
      roles: Array.from(profile.roles),
      artistIds: profile.artistIds,
      mentorIds: profile.mentorIds,
    });
  }).sort((a, b) => {
    const timeDiff = toMillis(b.walletUpdatedAt) - toMillis(a.walletUpdatedAt);
    if (timeDiff) return timeDiff;
    return String(a.name || a.phone).localeCompare(String(b.name || b.phone));
  });
}

function normalizeUserFilters(filters) {
  if (typeof filters === "string") {
    return { search: filters, role: "", blocked: "all" };
  }
  return {
    search: String(filters?.search || ""),
    role: String(filters?.role || ""),
    blocked: String(filters?.blocked || "all"),
  };
}

function settlementStatusForFilter(rawStatus) {
  const status = String(rawStatus || "").trim().toLowerCase();
  if (!status || status === "all") return "";
  if (status === "pending") return "requested";
  return status;
}

function normalizeSettlementShape(docSnap) {
  const data = docSnap.data() || {};
  const payoutAccount = data.payoutAccount || data.payout || {};
  const timeline = Array.isArray(data.timeline) ? data.timeline : [];

  return {
    id: docSnap.id,
    requestId: String(data.requestId || docSnap.id),
    userPhone: String(data.userPhone || ""),
    roleAtRequest: String(data.roleAtRequest || ""),
    name: String(data.name || ""),
    amount: num(data.amount),
    status: String(data.status || data.settlementStatus || "requested"),
    settlementStatus: String(data.settlementStatus || data.status || "requested"),
    payoutAccountId: String(data.payoutAccountId || ""),
    payoutAccount,
    settlementWindowMinHours: num(data.settlementWindowMinHours),
    settlementWindowMaxDays: num(data.settlementWindowMaxDays),
    settlementEtaFrom: data.settlementEtaFrom || null,
    settlementEtaTo: data.settlementEtaTo || null,
    settlementTxnId: String(data.settlementTxnId || ""),
    utrId: String(data.utrId || ""),
    settlementAt: data.settlementAt || null,
    adminNote: String(data.adminNote || ""),
    walletSyncDone: Boolean(data.walletSyncDone),
    walletSyncedAt: data.walletSyncedAt || null,
    settlementApplied: Boolean(data.settlementApplied),
    finalAmount: num(data.finalAmount || data.finalSettledAmount || 0),
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    timeline,
    raw: data,
  };
}

function normalizeTxOptions(optionsOrMax) {
  if (typeof optionsOrMax === "number") {
    return {
      maxItems: Math.max(20, Number(optionsOrMax) || 200),
      direction: "",
      sourceType: "",
      status: "",
      dateFrom: "",
      dateTo: "",
    };
  }
  return {
    maxItems: Math.max(20, Number(optionsOrMax?.maxItems) || 300),
    direction: String(optionsOrMax?.direction || ""),
    sourceType: String(optionsOrMax?.sourceType || ""),
    status: String(optionsOrMax?.status || ""),
    dateFrom: String(optionsOrMax?.dateFrom || ""),
    dateTo: String(optionsOrMax?.dateTo || ""),
  };
}

function timelineToRows(request) {
  const list = Array.isArray(request?.timeline) ? request.timeline : [];
  return list
    .map((x, idx) => ({
      id: `${request?.id || "request"}_${idx}`,
      status: String(x?.status || ""),
      note: String(x?.note || ""),
      settlementTxnId: String(x?.settlementTxnId || ""),
      utrId: String(x?.utrId || ""),
      actorName: String(x?.by?.name || ""),
      actorRole: String(x?.by?.role || ""),
      at: x?.at || null,
    }))
    .sort((a, b) => toMillis(b.at) - toMillis(a.at));
}

export function useWalletUsers(filters = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const normalized = normalizeUserFilters(filters);

  useEffect(() => {
    let active = true;
    const snapshots = {
      users: null,
      artists: null,
      mentors: null,
    };
    const applySnapshots = () => {
      if (!snapshots.users) return;
      const list = buildWalletRows(
        snapshots.users,
        snapshots.artists || [],
        snapshots.mentors || []
      );
      if (active) {
        setRows(list);
        setError("");
        setLoading(false);
      }
    };

    setLoading(true);
    const unsubUsers = onSnapshot(
      collection(db, "users"),
      (snap) => {
        snapshots.users = snap.docs;
        applySnapshots();
      },
      (err) => {
        Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "artists")).catch(() => ({ docs: [] })),
          getDocs(collection(db, "mentors")).catch(() => ({ docs: [] })),
        ])
          .then(([usersSnap, artistsSnap, mentorsSnap]) => {
            snapshots.users = usersSnap.docs;
            snapshots.artists = artistsSnap.docs;
            snapshots.mentors = mentorsSnap.docs;
            applySnapshots();
          })
          .catch((fallbackErr) => {
            if (!active) return;
            setError(fallbackErr?.message || err?.message || "Failed to load users");
            setLoading(false);
          });
      }
    );
    const unsubArtists = onSnapshot(collection(db, "artists"), (snap) => {
      snapshots.artists = snap.docs;
      applySnapshots();
    }, () => {
      snapshots.artists = [];
      applySnapshots();
    });
    const unsubMentors = onSnapshot(collection(db, "mentors"), (snap) => {
      snapshots.mentors = snap.docs;
      applySnapshots();
    }, () => {
      snapshots.mentors = [];
      applySnapshots();
    });

    Promise.all([
      getDocs(collection(db, "users")),
      getDocs(collection(db, "artists")).catch(() => ({ docs: [] })),
      getDocs(collection(db, "mentors")).catch(() => ({ docs: [] })),
    ])
      .then(([usersSnap, artistsSnap, mentorsSnap]) => {
        snapshots.users = usersSnap.docs;
        snapshots.artists = artistsSnap.docs;
        snapshots.mentors = mentorsSnap.docs;
        applySnapshots();
      })
      .catch(() => {});

    return () => {
      active = false;
      unsubUsers();
      unsubArtists();
      unsubMentors();
    };
  }, []);

  const filtered = useMemo(() => {
    const q = normalized.search.trim().toLowerCase();
    const roleQ = normalized.role.trim().toLowerCase();
    const blockedQ = boolFromFilter(normalized.blocked);

    return rows.filter((u) => {
      if (q) {
        const hay = `${u.phone} ${u.name} ${u.role}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (roleQ === "user" && (u.roles || []).some((r) => r === "artist" || r === "mentor")) return false;
      if (roleQ && roleQ !== "user" && !(u.roles || []).includes(roleQ)) return false;
      if (blockedQ != null && u.walletBlocked !== blockedQ) return false;
      return true;
    });
  }, [rows, normalized.blocked, normalized.role, normalized.search]);

  return { rows: filtered, loading, error };
}

export function useWalletUser(phone) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(phone));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!phone) {
      setUser(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, "users", String(phone)),
      (snap) => {
        setUser(snap.exists() ? walletUserShape(snap.id, snap.data() || {}) : null);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Failed to load wallet user");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [phone]);

  return { user, loading, error };
}

export function useWalletTransactions(phone, options = {}) {
  const opts = normalizeTxOptions(options);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(Boolean(phone));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!phone) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const qRef = query(
      collection(db, "users", String(phone), "wallet_transactions"),
      limit(opts.maxItems)
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const fromMs = normalizedDateStart(opts.dateFrom);
        const toMs = normalizedDateEnd(opts.dateTo);
        const directionQ = opts.direction.trim().toLowerCase();
        const sourceTypeQ = opts.sourceType.trim().toLowerCase();
        const statusQ = opts.status.trim().toLowerCase();

        const list = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => {
            const ms = toMillis(row.createdAt);
            if (ms && (ms < fromMs || ms > toMs)) return false;
            if (directionQ && String(row.direction || "").toLowerCase() !== directionQ) return false;
            if (sourceTypeQ && String(row.sourceType || "").toLowerCase() !== sourceTypeQ) return false;
            if (statusQ && String(row.status || "").toLowerCase() !== statusQ) return false;
            return true;
          })
          .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

        setRows(list);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Failed to load transactions");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [
    phone,
    opts.dateFrom,
    opts.dateTo,
    opts.direction,
    opts.maxItems,
    opts.sourceType,
    opts.status,
  ]);

  return { rows, loading, error };
}

export function useWalletTransactionsByRequest(phone, requestId) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(Boolean(phone && requestId));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!phone || !requestId) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const qRef = query(
      collection(db, "users", String(phone), "wallet_transactions"),
      where("requestId", "==", String(requestId)),
      limit(120)
    );
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Failed to load request transactions");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [phone, requestId]);

  return { rows, loading, error };
}

export function useWalletAudit({ phone = "", requestId = "", maxItems = 300 } = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    const qRef = query(collection(db, "admin_wallet_audit"), limit(Math.max(50, maxItems)));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const phoneQ = String(phone || "").trim();
        const requestQ = String(requestId || "").trim();
        const list = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => {
            if (phoneQ && String(row.targetPhone || "") !== phoneQ) return false;
            if (requestQ && String(row.requestId || "") !== requestQ) return false;
            return true;
          })
          .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Failed to load wallet audit timeline");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [maxItems, phone, requestId]);

  return { rows, loading, error };
}

export function useSettlementRequests(filters = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const {
    status = "all",
    role = "",
    phone = "",
    amountMin = "",
    amountMax = "",
    dateFrom = "",
    dateTo = "",
  } = filters || {};

  useEffect(() => {
    setLoading(true);
    const qRef = query(collection(db, "wallet_withdraw_requests"), limit(1000));
    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const statusQ = settlementStatusForFilter(status);
        const roleQ = String(role || "").trim().toLowerCase();
        const phoneQ = String(phone || "").trim();
        const min = Number(amountMin || 0);
        const max = Number(amountMax || Number.MAX_SAFE_INTEGER);
        const fromMs = normalizedDateStart(dateFrom);
        const toMs = normalizedDateEnd(dateTo);

        const list = snap.docs
          .map(normalizeSettlementShape)
          .filter((row) => {
            const rowStatus = _normalizeStatusAlias(row.status);
            if (statusQ && rowStatus !== statusQ) return false;
            if (roleQ && String(row.roleAtRequest || "").toLowerCase() !== roleQ) return false;
            if (phoneQ && !String(row.userPhone || "").includes(phoneQ)) return false;
            if (row.amount < min || row.amount > max) return false;
            const ms = toMillis(row.createdAt);
            if (ms && (ms < fromMs || ms > toMs)) return false;
            return true;
          })
          .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));

        setRows(list);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Failed to load settlement requests");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [amountMax, amountMin, dateFrom, dateTo, phone, role, status]);

  return { rows, loading, error };
}

function _normalizeStatusAlias(rawStatus) {
  const v = String(rawStatus || "").toLowerCase();
  if (v === "pending") return "requested";
  if (v === "approved") return "processing";
  return v || "requested";
}

export function useWithdrawRequests(status = "pending") {
  const normalizedStatus = String(status || "").toLowerCase() === "pending" ?
    "requested" :
    status;
  const { rows, loading, error } = useSettlementRequests({ status: normalizedStatus });
  return { rows, loading, error };
}

export function useOvertimeBookings({
  artist = "",
  reason = "",
  dateFrom = "",
  dateTo = "",
} = {}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    const qRef = query(
      collection(db, "artist_new_bookings"),
      where("overtimeRequestCount", ">", 0)
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const artistQ = String(artist || "").trim().toLowerCase();
        const reasonQ = String(reason || "").trim().toLowerCase();
        const fromMs = normalizedDateStart(dateFrom);
        const toMs = normalizedDateEnd(dateTo);

        const list = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((row) => {
            if (artistQ) {
              const hay = [
                row.artistName,
                row.artist_name,
                row.artistPhone,
                row.artist_uid,
                row.bookedToName,
              ].join(" ").toLowerCase();
              if (!hay.includes(artistQ)) return false;
            }
            if (reasonQ) {
              const note = `${row.overtimeLastReason || ""} ${row.overtimeLastNote || ""}`.toLowerCase();
              if (!note.includes(reasonQ)) return false;
            }
            const ts = toMillis(row.overtimeLastRequestedAt);
            if (ts && (ts < fromMs || ts > toMs)) return false;
            return true;
          })
          .sort((a, b) => toMillis(b.overtimeLastRequestedAt) - toMillis(a.overtimeLastRequestedAt));

        setRows(list);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || "Failed to load overtime bookings");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [artist, reason, dateFrom, dateTo]);

  return { rows, loading, error };
}

export function useWalletKpis() {
  const [kpis, setKpis] = useState({
    totalLiability: 0,
    totalOnHold: 0,
    totalPendingSettlements: 0,
    todayCreditedAmount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    let userTotals = { totalLiability: 0, totalOnHold: 0 };
    let requestRows = [];

    const recompute = () => {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      const end = new Date();
      end.setHours(23, 59, 59, 999);
      const startMs = start.getTime();
      const endMs = end.getTime();

      const totalPendingSettlements = requestRows
        .filter((r) => ["requested", "on_hold", "processing", "pending", "approved"]
          .includes(_normalizeStatusAlias(r.status)))
        .reduce((sum, r) => sum + num(r.amount), 0);

      const todayCreditedAmount = requestRows
        .filter((r) => _normalizeStatusAlias(r.status) === "credited")
        .reduce((sum, r) => {
          const ts = toMillis(r.settlementAt || r.updatedAt || r.createdAt);
          if (ts < startMs || ts > endMs) return sum;
          return sum + num(r.finalAmount || r.amount);
        }, 0);

      setKpis({
        totalLiability: userTotals.totalLiability,
        totalOnHold: userTotals.totalOnHold,
        totalPendingSettlements,
        todayCreditedAmount,
      });
      setLoading(false);
    };

    const unsubUsers = onSnapshot(
      collection(db, "users"),
      (usersSnap) => {
        const users = usersSnap.docs.map((d) => d.data() || {});
        userTotals = {
          totalLiability: users.reduce((sum, u) => sum + num(u.walletBalance), 0),
          totalOnHold: users.reduce((sum, u) => sum + num(u.walletOnHold), 0),
        };
        recompute();
      },
      (err) => {
        setError(err?.message || "Failed to load wallet KPIs");
        setLoading(false);
      }
    );

    const unsubRequests = onSnapshot(
      query(collection(db, "wallet_withdraw_requests"), limit(1000)),
      (reqSnap) => {
        requestRows = reqSnap.docs.map(normalizeSettlementShape);
        recompute();
      },
      (err) => {
        setError(err?.message || "Failed to load settlement KPIs");
        setLoading(false);
      }
    );

    return () => {
      unsubUsers();
      unsubRequests();
    };
  }, []);

  return { kpis, loading, error };
}

export function buildSettlementTimelineRows(request) {
  return timelineToRows(request);
}

export function formatDateTime(ts) {
  const ms = toMillis(ts);
  if (!ms) return "-";
  return new Date(ms).toLocaleString();
}

export function formatDateOnly(ts) {
  const ms = toMillis(ts);
  if (!ms) return "-";
  return new Date(ms).toLocaleDateString();
}

export function maskPayoutValue(account = {}) {
  const method = String(account?.method || "").toLowerCase();
  const upi = String(account?.upiId || "");
  const bank = String(account?.accountNumber || "");

  if (method.includes("upi")) {
    const [prefix = "", domain = ""] = upi.split("@");
    if (!prefix) return upi || "-";
    const safePrefix = prefix.length <= 2 ?
      `${prefix[0] || ""}*` :
      `${prefix.slice(0, 2)}***${prefix.slice(-1)}`;
    return domain ? `${safePrefix}@${domain}` : safePrefix;
  }

  if (!bank) return "-";
  if (bank.length <= 4) return "*".repeat(bank.length);
  return `${"*".repeat(Math.max(0, bank.length - 4))}${bank.slice(-4)}`;
}
